/**
 * @file Shared Axios instance with request/response interceptors.
 *
 * - Auth tokens are stored in httpOnly cookies (set by the backend).
 * - Response interceptor auto-unwraps `response.data`, handles 401 errors
 *   with a transparent cookie-based token-refresh queue, and shows toast
 *   messages for non-auth errors.
 *
 * Every client-side service file imports this instance instead of raw axios.
 *
 * @module services/api
 */
import axios from "axios";
import { toast } from "react-toastify";

/** @type {import('axios').AxiosInstance} Configured Axios instance (baseURL `/api/v1`). */
const api = axios.create({
  baseURL: "/api/v1",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Request interceptor — dev logging only (tokens are in httpOnly cookies)
 */
api.interceptors.request.use(
  (config) => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor — auto-extract data, handle 401 with cookie-based refresh
 */
/** Whether a token refresh request is currently in-flight. */
let isRefreshing = false;
/** Queue of failed requests waiting for the refresh to complete. */
let failedQueue = [];

/**
 * Resolve or reject every promise in the failed-request queue after a
 * token refresh attempt finishes.
 * @param {Error|null} error - The refresh error, or null on success.
 */
const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not a refresh/login/register/me request, try to refresh
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !originalRequest.url?.includes("/auth/login") &&
      !originalRequest.url?.includes("/auth/me") &&
      !originalRequest.url?.includes("/auth/patient/verify-code") &&
      !originalRequest.url?.includes("/auth/patient/request-phone-code") &&
      !originalRequest.url?.includes("/auth/patient/verify-phone-code") &&
      !originalRequest.url?.includes("/auth/patient/phone-register")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Refresh token is in httpOnly cookie — just POST, browser sends cookie automatically
        await axios.post("/api/v1/auth/refresh");
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Only redirect if not already on a public page
        const publicPaths = ["/login", "/register", "/verify-email", "/forgot-password", "/reset-password", "/patient/login", "/c/"];
        const isPublicPage = publicPaths.some((p) => window.location.pathname.startsWith(p));
        if (!isPublicPage) window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Show error toast for non-401 errors
    const message =
      error.response?.data?.message ||
      error.message ||
      "Ocurrió un error inesperado";

    // Don't toast on 401 (handled above)
    if (error.response?.status !== 401) {
      toast.error(message);
    }

    if (import.meta.env.DEV) {
      console.error("API Error:", {
        url: error.config?.url,
        status: error.response?.status,
        message,
      });
    }

    return Promise.reject(error);
  }
);

export default api;
